import type { ChatMessage, Conversation } from '@lemontaigu/chat';
import { receptionText } from './logic.js';

// Pure « À apprendre » logic (C9) — no React, no store, unit-tested in
// reviewLogic.test.ts. Turns the raw signals (escalated guest messages +
// flagged AI replies) into review items carrying everything the teach flow
// needs: the guest question, what the AI said, and the human's actual answer
// (the source the KB-fact drafter generalizes from).

export type ReviewReason = 'escalated' | 'flagged';

export interface ReviewItem {
  /** The message whose resolution is tracked (guest question, or flagged AI reply). */
  anchorId: number;
  conversationId: string;
  room: string;
  reason: ReviewReason;
  /** Guest question, French (bodyFr ?? body). */
  questionFr: string;
  /** What the AI replied, French — null when the AI never answered. */
  aiReplyFr: string | null;
  /** The human answer that followed in the thread, French — the teach source; null if none yet. */
  staffAnswerFr: string | null;
  createdAt: string;
  /** Already taught or dismissed (chat_review_resolved) — hidden unless staff opt to see handled items. */
  resolved: boolean;
}

export interface ReviewGroup {
  conversationId: string;
  room: string;
  items: ReviewItem[];
}

/** First staff message strictly after `afterId` in an id-ascending thread. */
function staffAnswerAfter(ordered: ChatMessage[], afterId: number): string | null {
  const staff = ordered.find((m) => m.id > afterId && m.sender === 'staff');
  return staff ? receptionText(staff) : null;
}

/** Nearest guest message strictly before `beforeId`. */
function guestQuestionBefore(ordered: ChatMessage[], beforeId: number): ChatMessage | null {
  let found: ChatMessage | null = null;
  for (const m of ordered) {
    if (m.id >= beforeId) break;
    if (m.sender === 'guest') found = m;
  }
  return found;
}

/**
 * Build the review queue from loaded data. Two signals, distinct by
 * construction (escalation is set when the AI KNOWS it can't answer; a flag
 * marks a reply the AI thought was fine) — so an exchange is normally one or
 * the other, and the two never share an anchor id.
 */
export function buildReviewQueue(
  conversations: Conversation[],
  messages: ChatMessage[],
  flaggedIds: Set<number>,
  resolvedIds: Set<number>,
): ReviewItem[] {
  const roomOf = new Map(conversations.map((c) => [c.id, c.room]));
  const byConversation = new Map<string, ChatMessage[]>();
  for (const m of messages) {
    const list = byConversation.get(m.conversationId);
    if (list) list.push(m);
    else byConversation.set(m.conversationId, [m]);
  }

  const items: ReviewItem[] = [];
  for (const [conversationId, msgs] of byConversation) {
    const ordered = [...msgs].sort((a, b) => a.id - b.id);
    const room = roomOf.get(conversationId) ?? '?';

    for (const m of ordered) {
      if (m.sender === 'guest' && m.escalated === true) {
        const aiReply = ordered.find((x) => x.id > m.id && x.sender === 'ai') ?? null;
        items.push({
          anchorId: m.id,
          conversationId,
          room,
          reason: 'escalated',
          questionFr: receptionText(m),
          aiReplyFr: aiReply ? receptionText(aiReply) : null,
          staffAnswerFr: staffAnswerAfter(ordered, m.id),
          createdAt: m.createdAt,
          resolved: resolvedIds.has(m.id),
        });
      } else if (m.sender === 'ai' && flaggedIds.has(m.id)) {
        const question = guestQuestionBefore(ordered, m.id);
        items.push({
          anchorId: m.id,
          conversationId,
          room,
          reason: 'flagged',
          questionFr: question ? receptionText(question) : '(question introuvable)',
          aiReplyFr: receptionText(m),
          staffAnswerFr: staffAnswerAfter(ordered, m.id),
          createdAt: m.createdAt,
          resolved: resolvedIds.has(m.id),
        });
      }
    }
  }

  // Most recent first — freshest gaps surface at the top.
  return items.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

/** Group items by conversation (room), preserving the recency order of first appearance. */
export function groupReview(items: ReviewItem[]): ReviewGroup[] {
  const groups = new Map<string, ReviewGroup>();
  for (const item of items) {
    const group = groups.get(item.conversationId);
    if (group) group.items.push(item);
    else
      groups.set(item.conversationId, {
        conversationId: item.conversationId,
        room: item.room,
        items: [item],
      });
  }
  return [...groups.values()];
}
